import React, { useCallback, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { normalizeApiError } from '../../api/apiError';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { ScreenHeader } from '../../components/ScreenHeader';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getManagedOrganization } from '../../services/managedOrganizationService';
import type { ManagedOrganization } from '../../types/managedOrganization';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'ManagedOrganizationDetails'>;
function Field({ label, value }: { label: string; value?: string | null }) {
  const palette = useAppColors();
  return <View style={styles.field}><Text style={[styles.label, { color: palette.textSecondary }]}>{label}</Text>
    <Text style={[styles.value, { color: palette.textPrimary }]}>{value || '—'}</Text></View>;
}
export function ManagedOrganizationDetailsScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const [organization, setOrganization] = useState<ManagedOrganization | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setOrganization(await getManagedOrganization(route.params.organizationId)); setError(''); }
    catch (caught) { setError(normalizeApiError(caught).message); }
    finally { setLoading(false); }
  }, [route.params.organizationId]);
  useFocusEffect(useCallback(() => { load().catch(() => undefined); }, [load]));
  if (!auth.roles?.includes('SUPER_ADMIN')) return <UnauthorizedScreen />;
  return <AppScreen scroll><ScreenHeader title="Organization Details" onBack={navigation.goBack} />
    <View style={styles.body}>
      {loading ? <ActivityIndicator color={palette.primary} /> : null}
      {error ? <ErrorState message={error} onRetry={load} /> : null}
      {organization ? <>
        <AppCard style={styles.card}>
          <Field label="Organization Code" value={organization.organizationCode} />
          <Field label="Organization Name" value={organization.organizationName} />
          <Field label="Legal Name" value={organization.legalName} />
          <Field label="Official Email" value={organization.officialEmail} />
          <Field label="Country" value={organization.countryCode} />
          <Field label="Time Zone" value={organization.timeZone} />
          <Field label="Currency" value={organization.defaultCurrencyCode} />
          <Field label="Status" value={organization.isActive ? 'Active' : 'Inactive'} />
        </AppCard>
        <AppButton title="Create HR Admin" onPress={() => navigation.navigate(
          routes.organizationHrAdminCreate,
          { organizationId: organization.organizationId, organizationName: organization.organizationName })} />
      </> : null}
    </View></AppScreen>;
}
const styles = StyleSheet.create({
  body: { padding: spacing.lg, gap: spacing.lg }, card: { gap: spacing.md },
  field: { gap: spacing.xs }, label: { ...typography.caption },
  value: { ...typography.body, fontWeight: '600' },
});
